import React, { FunctionComponent, useEffect, useState } from 'react';
import { Divider } from '@material-ui/core';
import useCarPrices from '../../../../../../hooks/prices/useCarPrices';
import useTotalPrices from '../../../../../../hooks/prices/useTotalPrice';
import usePrice from '../../../../../../../gqlCommon/hooks/prices/usePrice';
import usePriceValidation from '../../../../../../../gqlCommon/hooks/currency/usePriceValidation';
import { usePersistedCurrencyPreference } from '../../../../../../../gqlCommon/hooks/currency/usePersistedCurrencyPreference';
import useTheme from '@material-ui/core/styles/useTheme';
import { ISpcTheme } from '../../../../../../../common/theme/ISpcTheme';
import FinanceCalculatorSpcContainer from '../../../../../../../gqlCommon/components/financeCalculator/FinanceCalculatorSpcContainer';
import { useFinanceCarConfiguration } from '../../../../../../../gqlCommon/hooks/useFinanceCarConfiguration';
import SummaryFinancePrice from './summaryFinancePrice/SummaryFinancePrice';
import SummaryFinanceTermRate from './summaryFinanceTermRate/SummaryFinanceTermRate';
import SummaryFinanceComparisonRate from './summaryFinanceComparisonRate/SummaryFinanceComparisonRate';
import SummaryFinanceChosenRate from './summaryFinanceChosenRate/SummaryFinanceChosenRate';
import { IFinanceCarConfiguration } from '../../../../../../../gqlCommon/model/financeWidgetConfig/IFinanceCarConfiguration';
import { FinanceResultType } from '../../../../../../../__generated__/globalTypes';
import { PriceDisplayType } from '../../../../../../../../../common/enum/PriceDisplayType';
import Translation from '../../../../../../../common/components/translation/Translation';
import SummaryLabelWithPrice from '../../summaryLabelWithPrice/SummaryLabelWithPrice';
import useInterestRate from '../../../../../../hooks/useInterestRate';
import SummaryFinanceMileage from './summaryFinanceMileage/SummaryFinanceMileage';
import SummaryFinanceProduct from './summaryFinanceProduct/SummaryFinanceProduct';
import useCustomizeCarContext from '../../../../../../hooks/useCustomizeCarContext';

const SummaryFinance: FunctionComponent = () => {
  const {
    spc: {
      customise: {
        displaySummaryTerm,
        displayFinanceDeposit,
        displayInterestRate,
        displayInsurance,
        displayTotalFinancingAmount,
        displayFinanceProduct,
      },
    },
  } = useTheme<ISpcTheme>();
  const { selectedCar, loading } = useCustomizeCarContext();
  const isCash = selectedCar?.finance?.type === FinanceResultType.cash;
  const residualValue = selectedCar?.finance?.simulation?.residualValue?.amount;
  const hasResidualValue = selectedCar?.finance?.type === FinanceResultType.finance && !!residualValue;
  const [financeCarConfiguration, setFinanceCarConfiguration] = useState<IFinanceCarConfiguration>();
  const { isSecondaryCurrencyDisplay } = usePersistedCurrencyPreference();
  const { originalPrice } = useCarPrices(selectedCar?.prices);
  const { totalPrices } = useTotalPrices(selectedCar?.prices.totalPrice);
  const { isMultiCurrency } = usePriceValidation(originalPrice);
  const shouldDisplaySecondaryCurrency = isMultiCurrency && isSecondaryCurrencyDisplay;
  const { primaryPrice, secondaryPrice } = usePrice(totalPrices.length ? totalPrices : originalPrice);
  const currency = shouldDisplaySecondaryCurrency ? secondaryPrice.money.currency : primaryPrice.money.currency;
  const interestRate = useInterestRate();
  const monthlyPayment = selectedCar?.finance?.simulation?.paymentValue;
  const comparisonRate = selectedCar?.finance?.simulation?.comparisonRate;
  const deposits = selectedCar?.finance?.simulation?.deposits || [];
  const depositsValue = deposits[shouldDisplaySecondaryCurrency ? 1 : 0]?.value;
  const term = selectedCar?.finance?.simulation?.loanTermPeriod.value;
  const insurance = selectedCar?.car.attributes.insurance;
  const shouldDisplayTotalFinancingAmount = displayTotalFinancingAmount && deposits;
  const totalFinancingAmount = shouldDisplayTotalFinancingAmount
    ? selectedCar?.finance?.simulation?.financeAmount[shouldDisplaySecondaryCurrency ? 1 : 0].amount
    : 0;
  const frequencyTranslation = selectedCar?.finance?.simulation?.frequency;
  const taxLabel = selectedCar?.car?.attributes?.priceLabel;
  const mileage = selectedCar?.finance?.simulation?.mileage;
  const financeProduct = selectedCar?.finance?.simulation?.financeProduct;
  const { getFinanceCarConfiguration } = useFinanceCarConfiguration();

  useEffect(() => {
    if (selectedCar) {
      setFinanceCarConfiguration(
        getFinanceCarConfiguration(
          selectedCar?.car.code,
          selectedCar?.prices.carPrice.originalPrice,
          selectedCar?.car.trim.attributes.label,
          selectedCar?.car.variant?.attributes.name,
        ),
      );
    }
  }, [selectedCar]);

  return (
    <>
      {!isCash && (
        <>
          <Divider />
          {displayFinanceDeposit && (
            <SummaryLabelWithPrice
              label={<Translation id={'trans__configurator__customise_page_finance_details__upfront_payment_label'} />}
              loading={loading}
              price={depositsValue}
              priceType={PriceDisplayType.FINANCE}
              currency={currency}
              testId={'deposit'}
            />
          )}
          {displayFinanceProduct && (
            <SummaryFinanceProduct financeProduct={financeProduct} dataTest="summary:financeProduct" />
          )}
          {hasResidualValue && (
            <SummaryLabelWithPrice
              label={
                <Translation id={'trans__configurator__customise_page_finance_details__summary_residual_value_label'} />
              }
              loading={loading}
              price={residualValue}
              priceType={PriceDisplayType.FINANCE}
              currency={currency}
              testId={'residual_value'}
              indicativeNumber="trans__configurator__indicative_numbers__residualValue"
            />
          )}
          {shouldDisplayTotalFinancingAmount && (
            <SummaryLabelWithPrice
              label={
                <Translation
                  id={'trans__configurator__customise_page_finance_details__summary_total_financing_amount_label'}
                />
              }
              loading={loading}
              price={totalFinancingAmount}
              priceType={PriceDisplayType.FINANCE}
              currency={currency}
              testId={'total_financing_amount'}
            />
          )}
          {displaySummaryTerm && !!term && <SummaryFinanceTermRate term={term} dataTest="summary:termRate" />}
          {mileage && <SummaryFinanceMileage mileage={mileage} dataTest="summary:mileage" />}
          {displayInterestRate && !!parseFloat(interestRate) && (
            <SummaryFinanceChosenRate interestRate={interestRate} />
          )}
          {displayInsurance && (
            <SummaryLabelWithPrice
              label={
                <Translation
                  id={'trans__configurator__customise_page_finance_details__summary_insurance_amount_label'}
                />
              }
              loading={loading}
              price={insurance?.[0]?.amount}
              priceType={PriceDisplayType.FINANCE}
              currency={currency}
              testId={'insurance'}
            />
          )}
          <SummaryFinancePrice
            loading={loading}
            prices={monthlyPayment}
            priceType={PriceDisplayType.FINANCE}
            currency={currency}
            comparisonRate={comparisonRate}
            testId={'repayment_frequency'}
            frequencyTranslation={frequencyTranslation}
            taxLabel={taxLabel}
          />
          {!!comparisonRate && (
            <SummaryFinanceComparisonRate
              loading={loading}
              percentage={comparisonRate}
              percentageType={'comparisonRate'}
            />
          )}
          {financeCarConfiguration && (
            <FinanceCalculatorSpcContainer financeCarConfiguration={financeCarConfiguration} />
          )}
        </>
      )}
    </>
  );
};

export default SummaryFinance;
